<template>
  <div>
    <div class="p-2">
      <div v-if="!order.id" class="p-3 text-center text-muted">No order selected</div>

      <div v-else>
        <div class="receive-header d-flex justify-content-between align-items-start mb-3">
          <div class="vendor-block">
            <div class="small text-muted">Vendor</div>
            <div class="h6 mb-1">{{ (order.vendor?.first_name || '') + ' ' + (order.vendor?.last_name || '') || 'Vendor' }}</div>
            <div class="small text-muted">{{ order.vendor?.company_name || order.vendor?.external_id || '' }}</div>
            <div class="small mt-1">{{ order.vendor?.phone || '' }}{{ order.vendor?.email ? ' • ' + order.vendor.email : '' }}</div>
          </div>

          <div class="po-block text-end">
            <div class="small text-muted">Purchase Order</div>
            <div class="h6 mb-1">{{ order.name || order.external_id }}</div>
            <div class="small">PO#: {{ order.external_id || '-' }}</div>
            <div class="small">Created: {{ order.created_at || '-' }}</div>
            <div class="small">Expected: {{ order.expected_delivery_date || '-' }}</div>
            <div class="h6 mt-2">{{ order.total_price ? ('$' + Number(order.total_price).toFixed(2)) : '-' }}</div>
          </div>
        </div>

        <div class="table-responsive mb-3">
          <table class="table table-sm items-table invoice-style border-0">
            <thead style="background: #f8f9fa">
              <tr>
                <th style="width:40px">#</th>
                <th  style="width: 40%;">Item</th>
                <th class="text-center" style="width:20%">Ordered</th>
                <th class="text-center" style="width:20%">Received</th>
                <th class="text-center" style="width:20%">Status</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(it, idx) in form.items" :key="it.itemable_id || idx">
                <tr class="item-card-row">
                  <td colspan="5" class="p-0">
                    <div class="item-card">
                      <div class="item-card-header d-flex flex-column flex-lg-row justify-content-between gap-3 p-3">
                        <div class="item-info d-flex gap-3 align-items-start flex-grow-1">
                          <span class="badge bg-secondary mt-1">{{ idx + 1 }}</span>
                          <div>
                            <div class="item-name">{{ it.name }}</div>
                            <div class="item-code text-muted small">{{ it.code }}</div>
                          </div>
                        </div>

                        <div class="item-summary-grid d-flex flex-column flex-sm-row gap-3 align-items-start text-center flex-grow-1">
                          <div class="summary-cell">
                            <div class="summary-value">{{ it.quantity }} {{ it.unit }}</div>
                            <!-- <div class="summary-label">Ordered</div> -->
                          </div>
                          <div class="summary-cell">
                            <input type="number"
                              class="form-control form-control-thin"
                              placeholder="Received quantity"
                              :name="`items[${idx}][received_quantity]`"
                              :id="`items_${idx}_received_quantity`"
                              :ref="`items_${idx}_received_quantity`"
                              v-model.number="it.received_quantity"
                              :min="0"
                              :disabled="['partial_received','received'].includes(it.receive_status)" />
                           
                          </div>
                          <div class="summary-cell">
                            <template v-if="['partial_received','received'].includes(it.receive_status)">
                              <span class="badge bg-secondary">Already received</span>
                            </template>
                            <template v-else-if="it.received_quantity !== null && it.received_quantity !== ''">
                              <span v-if="it.received_quantity === it.quantity" class="badge bg-success">Matched</span>
                              <span v-else-if="it.received_quantity > it.quantity" class="badge bg-warning text-dark">Over Received</span>
                              <span v-else class="badge bg-danger">Quantity not matched</span>
                            </template>
                            
                          </div>
                        </div>
                      </div>

                      <div class="item-card-body p-3 border-top">
                        <div class="item-flags-container">
                          <div class="mb-2">
                            <span v-if="it.material_type === 'chemical'" class="badge bg-warning text-dark">
                              <i class="fa fa-flask me-1"></i>Chemical
                            </span>
                            <span v-else-if="it.material_type === 'part'" class="badge bg-info text-dark">
                              <i class="fa fa-wrench me-1"></i>Part
                            </span>
                          </div>

                          <div v-if="it.material_type === 'chemical' && it.sds === 'yes'" class="mb-2">
                            <label class="form-label form-label-thin small mb-1">
                              <i class="fa fa-file-pdf text-danger me-1"></i>Attach Safety Data Sheet (SDS) <span class="text-danger">*</span>
                            </label>
                            <input
                              type="file"
                              class="form-control form-control-thin"
                              :name="`items[${idx}][sds_attachment]`"
                              :id="`items_${idx}_sds_attachment`"
                              :ref="`items_${idx}_sds_attachment`"
                              @change="(e) => onSdsFileChange(idx, e)"
                              accept=".pdf,.doc,.docx"
                            />
                            <small class="text-muted d-block mt-1">Accepted: PDF, DOC, DOCX</small>
                          </div>

                          <div class="material-flags-summary small">
                            <span v-if="it.material_type === 'chemical'">
                              <span v-if="it.sds === 'yes'" class="badge bg-light text-dark me-1">
                                <i class="fa fa-check text-success"></i> SDS Required
                              </span>
                              <span v-if="it.approved_chemical === 'yes'" class="badge bg-light text-dark me-1">
                                <i class="fa fa-check text-success"></i> Approved
                              </span>
                              <span v-if="it.food_grade_chemical === 'yes'" class="badge bg-light text-dark me-1">
                                <i class="fa fa-check text-success"></i> Food Grade
                              </span>
                            </span>
                            <span v-if="it.material_type === 'part'">
                              <span v-if="it.food_contact_part === 'yes'" class="badge bg-light text-dark me-1">
                                <i class="fa fa-check text-success"></i> Food Contact
                              </span>
                               <span v-else class="badge bg-light text-dark me-1">
                                <i class="fa fa-times text-danger"></i> No Food Contact
                              </span>
                            </span>
                          </div>

                          <div v-if="it.checklist" class="equipment-checklist mt-2">
                            <div class="mb-2 small text-muted">Equipment Sanitation Checklist</div>
                            <div class="checklist-rows">
                              <div v-for="(question, qIdx) in checklistQuestions" :key="question.key" class="equipment-checklist-item">
                                <div class="checklist-serial">{{ qIdx + 1 }}</div>
                                <div class="d-flex align-items-center justify-content-between flex-column flex-md-row gap-3 flex-grow-1">
                                  <div class="question-label flex-grow-1">
                                    <label class="form-label form-label-thin mb-0">{{ question.label }}</label>
                                  </div>
                                  <div class="radio-group d-flex flex-wrap gap-3 align-items-center">
                                    <label class="radio-inline mb-0">
                                      <input type="radio"
                                        :name="`items_${idx}_checklist_${question.key}`"
                                        :value="'yes'"
                                        v-model="it.checklist[question.key]"
                                      />
                                      <span class="ms-1 fw-bold text-success" style="font-size: 0.9rem;">YES</span>
                                    </label>
                                    <label class="radio-inline mb-0">
                                      <input type="radio"
                                        :name="`items_${idx}_checklist_${question.key}`"
                                        :value="'no'"
                                        v-model="it.checklist[question.key]"
                                      />
                                      <span class="ms-1 fw-bold text-danger" style="font-size: 0.9rem;">NO</span>
                                    </label>
                                    <label class="radio-inline mb-0">
                                      <input type="radio"
                                        :name="`items_${idx}_checklist_${question.key}`"
                                        :value="'na'"
                                        v-model="it.checklist[question.key]"
                                      />
                                      <span class="ms-1 fw-bold text-warning" style="font-size: 0.9rem;">N/A</span>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
              <tr v-if="!form.items.length">
                <td colspan="5" class="text-center text-muted py-3">No items</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="mb-4">
          <FileUploader v-model:files="form.files" :help-text="'Attach order-related files or receipts.'" />
        </div>

        <div class="mb-3">
          <label class="form-label form-label-thin">Received Date</label>
          <input type="date" class="form-control form-control-thin" name="received_date" id="received_date" ref="received_date" v-model="form.received_date" />
        </div>

        <!-- Images Section -->
        <div v-if="order.images && order.images.length" class="mb-3">
          <label class="form-label form-label-thin">Images</label>
          <div class="d-flex flex-wrap gap-2">
            <div v-for="image in order.images" :key="image.id" class="attachment-tile text-center" style="width: 100px; cursor: pointer;" @click="openFile(image.file_path)">
              <div class="attachment-preview mb-2 rounded border d-flex align-items-center justify-content-center" style="width: 100%; height: 80px; background-color: #f0f0f0; overflow: hidden;">
                <img :src="getFileUrl(image.file_path)" :alt="image.file_name" style="max-width: 100%; max-height: 100%; object-fit: cover;" />
              </div>
              <small class="text-truncate" style="display: block; width: 100%; font-size: 11px;">{{ image.file_name }}</small>
              <small class="text-muted" style="display: block; font-size: 10px;">{{ formatFileSize(image.file_size) }}</small>
            </div>
          </div>
        </div>

        <!-- Files Section -->
        <div v-if="order.files && order.files.length" class="mb-3">
          <label class="form-label form-label-thin">Files</label>
          <div class="d-flex flex-wrap gap-2">
            <div v-for="file in order.files" :key="file.id" class="attachment-tile text-center" style="width: 100px; cursor: pointer;" @click="openFile(file.file_path)">
              <div class="attachment-preview mb-2 rounded border d-flex align-items-center justify-content-center" style="width: 100%; height: 80px; background-color: #f8f9fa;">
                <div>
                  <i :class="getFileIcon(file.file_name)" style="font-size: 32px; color: #667eea;"></i>
                </div>
              </div>
              <small class="text-truncate" style="display: block; width: 100%; font-size: 11px;">{{ file.file_name }}</small>
              <small class="text-muted" style="display: block; font-size: 10px;">{{ formatFileSize(file.file_size) }}</small>
            </div>
          </div>
        </div>

        <div class="mb-3">
          <label class="form-label form-label-thin">Notes</label>
          <textarea class="form-control form-control-thin" name="notes" id="notes" ref="notes" v-model="form.notes" rows="2" placeholder="Optional notes"></textarea>
        </div>

        <div class="mb-3">
          <LogsTimeline :logs="order.logs || []" />
        </div>

        <div class="d-flex justify-content-end gap-2">
          <button class="btn btn-sm btn-outline-secondary" @click="close">Cancel</button>
          <button class="btn btn-sm btn-primary" :disabled="submitting || orderAlreadyReceived" @click="submitReceive">
            {{ orderAlreadyReceived ? 'Already received' : (submitting ? 'Saving...' : 'Confirm Receive') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import LogsTimeline from '@/components/LogsTimeline.vue'
import FileUploader from '@/components/FileUploader.vue'

export default {
  name: 'ReceiveOrderModal',
  components: { LogsTimeline, FileUploader },
  data() {
    return {
      order: {},
      form: {
        items: [],
        files: [],
        received_date: '',
        notes: ''
      },
      checklistQuestions: [
        { key: 'food_grade_materials', label: 'Food-grade materials used' },
        { key: 'surfaces_smooth_cleanable', label: 'Surfaces smooth and cleanable' },
        { key: 'welds_continuous_sanitary', label: 'Welds continuous and sanitary' },
        { key: 'no_cracks_pits_crevices', label: 'No cracks, pits, or crevices' },
        { key: 'equipment_fully_drainable', label: 'Equipment fully drainable' },
        { key: 'accessible_for_cleaning_inspection', label: 'Accessible for cleaning and inspection' },
        { key: 'no_product_water_accumulation', label: 'No product or water accumulation points' },
        { key: 'hygienic_fasteners_used', label: 'Hygienic fasteners used' },
        { key: 'utilities_routed_hygienically', label: 'Utilities routed hygienically' },
        { key: 'maintenance_friendly_design', label: 'Maintenance-friendly design' },
        { key: 'pest_harbor_eliminated', label: 'Pest harborage eliminated' },
        { key: 'cleaning_validation_possible', label: 'Cleaning validation possible' },
        { key: 'meets_sanitary_standards', label: 'Meets applicable sanitary standards' }
      ],
      submitting: false
    }
  },
  computed: {
    orderAlreadyReceived() {
      return ['partial_received', 'received'].includes(String(this.order.receive_status || '').toLowerCase())
    },

  },
  methods: {
    setOrder(order) {
      this.order = order || {}
      // map items; support different material property names and include unit and receive_status
      this.form.items = (this.order.items || []).map(i => ({
        purchase_order_item_id: i.id || null,
        itemable_id: i.itemable_id || i.itemable?.id || null,
        name: i.get_material?.name || i.get_materal?.name || i.get_materials?.name || i.itemable?.name || i.itemable_name || '',
        code: i.get_material?.code || i.get_materal?.code || i.itemable?.code || '',
        unit: i.unit || i.get_material?.unit || i.get_materal?.unit || i.itemable?.unit || '',
        quantity: Number(i.quantity || 0),
        received_quantity: i.received_quantity !== undefined && i.received_quantity !== null ? Number(i.received_quantity) : null,
        receive_status: i.receive_status || '',
        material_type: i.material_type || '',
        sds: i.sds || 'no',
        approved_chemical: i.approved_chemical || 'no',
        food_grade_chemical: i.food_grade_chemical || 'no',
        food_contact_part: i.food_contact_part || 'no',
        sds_attachment: null,
        checklist: (i.material_type || '').toLowerCase() === 'equipment' ? this.getDefaultChecklist() : null
      }))
      this.form.received_date = this.order.actual_delivery_date ? String(this.order.actual_delivery_date).substr(0,10) : (new Date()).toISOString().substr(0,10)
      this.form.notes = ''
      this.form.files = []
    },
    getDefaultChecklist() {
      return this.checklistQuestions.reduce((result, question) => {
        result[question.key] = 'na'
        return result
      }, {})
    },
    getChecklistLabel(key) {
      return this.checklistQuestions.find(question => question.key === key)?.label || key
    },
    close() {
      this.$emit('close')
      // parent FormModal will close
    },
    getFileUrl(filePath) {
      if (!filePath) return ''
      if (filePath.startsWith('http')) return filePath
      const baseUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000'
      return `${baseUrl}/${filePath}`
    },
    getFileIcon(fileName) {
      const ext = (fileName || '').split('.').pop().toLowerCase()
      const iconMap = {
        'pdf': 'fas fa-file-pdf',
        'doc': 'fas fa-file-word',
        'docx': 'fas fa-file-word',
        'xls': 'fas fa-file-excel',
        'xlsx': 'fas fa-file-excel',
        'ppt': 'fas fa-file-powerpoint',
        'pptx': 'fas fa-file-powerpoint',
        'txt': 'fas fa-file-text',
        'zip': 'fas fa-file-archive',
        'rar': 'fas fa-file-archive',
        'csv': 'fas fa-file-csv',
        'json': 'fas fa-file-code',
        'xml': 'fas fa-file-code',
        'jpg': 'fas fa-file-image',
        'jpeg': 'fas fa-file-image',
        'png': 'fas fa-file-image',
        'gif': 'fas fa-file-image',
        'webp': 'fas fa-file-image'
      }
      return iconMap[ext] || 'fas fa-file'
    },
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
    },
    openFile(filePath) {
      const url = this.getFileUrl(filePath)
      window.open(url, '_blank')
    },
    onSdsFileChange(index, event) {
      const file = event.target.files[0]
      if (file) {
        this.form.items[index].sds_attachment = file
      }
    },
    async submitReceive() {
      this.submitting = true
      const formData = new FormData()
      formData.append('purchase_order_id', this.order.id)
      formData.append('received_date', this.form.received_date)
      formData.append('notes', this.form.notes)
      
      this.form.items.forEach((it, idx) => {
        if (it.purchase_order_item_id !== null) {
          formData.append(`items[${idx}][purchase_order_item_id]`, it.purchase_order_item_id)
        }
        if (it.itemable_id !== null) {
          formData.append(`items[${idx}][itemable_id]`, it.itemable_id)
        }
        formData.append(`items[${idx}][received_quantity]`, it.received_quantity)
        if (it.sds_attachment) {
          formData.append(`items[${idx}][sds_attachment]`, it.sds_attachment)
        }
        if (it.checklist) {
          Object.keys(it.checklist).forEach(key => {
            formData.append(`items[${idx}][checklist][${key}][label]`, this.getChecklistLabel(key))
            formData.append(`items[${idx}][checklist][${key}][value]`, it.checklist[key] || 'na')
          })
        }
      })

      const files = Array.isArray(this.form.files) ? this.form.files : []
      files.forEach(file => {
        formData.append('files[]', file)
      })

      const payload = formData

      const result = await form_api_submit({
        url: 'purchase-order/receive',
        method: 'POST',
        data: payload,
        show_success_toast: true,
        show_error_toast: true
      })

      if (result.success) {
        this.$emit('received', result.data)
        this.close()
      }

      this.submitting = false
    }
  }
}
</script>

<style scoped>
.item-name { font-weight:500 }
.item-code { font-family: monospace; font-size: 12px }

.receive-header {
  border-bottom: 1px solid #eef2f6;
  padding-bottom: 12px;
}
.vendor-block .h6, .po-block .h6 { font-size: 16px; font-weight: 700 }
.po-block .small { color: #6b7280 }

.invoice-style th {
  background: transparent;
  color: #374151;
  font-weight: 700;
}
.invoice-style td { vertical-align: middle;
 background: transparent; }
.items-table, .items-table th, .items-table td {
  border: 0 !important;
}

.badge {
  padding: 6px 8px;
  border-radius: 999px;
  font-size: 12px;
}

.text-muted.small { color: #6b7280 }

/* Item flags row styling */
.item-flags-row {
  border-top: 1px solid #e5e7eb;
}

.item-card-row {
  border: 0 !important;
}

.item-card {
  border: 1px solid #dee2e6;
  border-radius: 1rem;
  background: #ffffff;
  overflow: hidden;
  margin-bottom: 1rem;
}

.item-card-header {
  gap: 1rem;
}

.item-info {
  flex: 1 1 42%;
  max-width: 42%;
}

.item-card-header .item-name {
  font-size: 0.95rem;
  font-weight: 500;
}

.item-card-header .item-code {
  color: #6c757d;
}

.item-card-body {
  background: #fbfcfd;
}

.item-summary-grid {
  flex: 1 1 58%;
  min-width: 0;
  justify-content: space-between;
  align-items: center;
}

.summary-cell {
  flex: 1;
  min-width: 110px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.summary-value {
  font-weight: 500;
  color: #111827;
}

.summary-label {
  font-size: 0.75rem;
  color: #6b7280;
}

.item-flags-container {
  padding: 0;
}

.equipment-checklist {
  padding: 0.75rem 0 0;
}

.checklist-rows {
  display: grid;
  gap: 0.5rem;
}

.equipment-checklist-item {
  border: 1px solid #e9ecef;
  border-radius: 0.75rem;
  background: #ffffff;
  padding: 0.85rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.checklist-serial {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f3f4f6;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  flex-shrink: 0;
}

.question-label {
  min-width: 220px;
}

.radio-group {
  min-width: 240px;
}

.radio-inline {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0;
}

.radio-inline input[type="radio"] {
  cursor: pointer;
  width: 16px;
  height: 16px;
  margin: 0;
}

.radio-inline span {
  font-size: 0.9rem;
  font-weight: 700;
}

.material-flags-summary {
  margin-top: 0.5rem;
}

</style>
